import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'
import {
    FieldTypeSelectorComponent,
    FieldTypeSelectorData,
} from '@shared/field-type-selector/field-type-selector.component'
import { ModalContainerComponent } from '@components-library/tb-modal-manager/modal-container-component/modal-container.component'
import { AddFieldData, Field, FieldEntities } from '@models/ui'
import { UntilDestroy } from '@ngneat/until-destroy'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { FieldConfigurationTypeComponent } from '@app/feature/header/workspace-controls/advanced-options/field-section/field-configuration-type/field-configuration-type.component'
import { TranslocoModule } from '@jsverse/transloco'
import { TbTabsComponent } from '@components-library/tb-tabs/tb-tabs.component'
import { ModalLayoutComponent } from '@components-library/tb-modal-manager/modal-layout/modal-layout.component'
import { ModalLayoutButtonsComponent } from '@components-library/tb-modal-manager/modal-layout-buttons/modal-layout-buttons.component'
import { TbIconComponent } from '@components-library/tb-icon/tb-icon.component'
import { TbTab } from '@components-library/tb-tab/tb-tab.component'
import { TbDividerComponent } from '@components-library/tb-divider/tb-divider.component'
import { keyBy, keys, reduce } from 'lodash-es'
import { FieldTypeFactoryService } from '@services/factories/field-type/field-type-factory.service'
import { generateUuid } from '@core/global-util'
import { AddFieldComponent } from '@app/feature/add-field/add-field.component'

export interface FieldData {
    schemaTabs: TbTab[]
    fieldBySchema: Record<string, FieldEntities>
    inactiveFields: string[]
}

export interface FieldConfigurationOutput {
    fieldBySchema: Record<string, FieldEntities>
    inactiveFields: string[]
}

type FieldsByTab = Record<
    string,
    {
        templateFields: Field[]
        newFields: Field[]
    }
>

@UntilDestroy()
@Component({
    selector: 'app-field-configuration',
    templateUrl: './field-configuration.component.html',
    styleUrls: ['./field-configuration.component.sass'],
    imports: [
        TranslocoModule,
        FieldConfigurationTypeComponent,
        TbButtonComponent,
        TbTabsComponent,
        ModalLayoutComponent,
        ModalLayoutButtonsComponent,
        TbIconComponent,
        TbDividerComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldConfigurationComponent
    extends ModalContainerComponent<FieldData, FieldConfigurationOutput>
    implements OnInit
{
    private readonly fieldTypeFactoryService = inject(FieldTypeFactoryService)

    tabs!: TbTab[]
    selectedTab!: TbTab
    fieldsByTab!: FieldsByTab
    inactiveFields!: string[]
    fieldsChanged = false

    getFieldIcon(fieldType: string) {
        return this.fieldTypeFactoryService.getIconByFieldType(fieldType)
    }

    ngOnInit() {
        this.tabs = this.data.schemaTabs
        this.selectedTab = this.tabs[0]
        this.fieldsByTab = this.getFieldsByTab()
        this.inactiveFields = [...this.data.inactiveFields]
    }

    applyChanges() {
        const fieldBySchema = keys(this.data.fieldBySchema).reduce(
            (acc, guid) => {
                acc[guid] = this.combineFields(guid)
                return acc
            },
            {} as Record<string, FieldEntities>,
        )

        this.close({
            fieldBySchema,
            inactiveFields: this.inactiveFields,
        })
    }

    activeChanged(active: boolean, guid: string) {
        if (!this.fieldsChanged) this.fieldsChanged = true
        if (!active) {
            this.inactiveFields.push(guid)
            return
        }

        if (!this.inactiveFields.includes(guid)) return

        const index = this.inactiveFields.findIndex((fieldGuid) => fieldGuid === guid)
        this.inactiveFields.splice(index, 1)
    }

    editField(field: Field, key: 'templateFields' | 'newFields') {
        this.modalManagerService
            .openDialog<AddFieldComponent, AddFieldData, Field>({
                component: AddFieldComponent,
                data: {
                    ...field,
                    new_workspace: true,
                },
            })
            .subscribe((data) => {
                if (!data) return
                if (!this.fieldsChanged) this.fieldsChanged = true

                const fields = this.fieldsByTab[this.selectedTab.id][key]

                const index = fields.findIndex((field) => field.guid === data.guid)

                fields.splice(index, 1, data)
            })
    }

    addField() {
        const existingFields = this.combineFields(this.selectedTab.id)
        this.modalManagerService
            .openDialog<FieldTypeSelectorComponent, FieldTypeSelectorData, Field | undefined>({
                component: FieldTypeSelectorComponent,
                data: {
                    newWorkspace: true,
                    existingFields,
                },
            })
            .subscribe((field) => {
                if (field) {
                    if (!this.fieldsChanged) this.fieldsChanged = true
                    field.guid = generateUuid()
                    this.fieldsByTab[this.selectedTab.id].newFields.push(field)
                }
            })
    }

    hasChanged(): boolean {
        return this.fieldsChanged
    }

    private combineFields(guid: string) {
        const allFields = this.fieldsByTab[guid].templateFields.concat(
            this.fieldsByTab[guid].newFields,
        )
        return keyBy(allFields, 'guid')
    }

    private getFieldsByTab() {
        return this.tabs.reduce((acc, tab) => {
            const fields = this.getFields(tab.id)
            if (!fields) return acc

            acc[tab.id] = fields
            return acc
        }, {} as FieldsByTab)
    }

    private getFields(schemaGuid: string) {
        const fieldEntities = this.data.fieldBySchema[schemaGuid]
        if (!fieldEntities) return

        return reduce(
            fieldEntities,
            (acc, field) => {
                if ('new_workspace' in field) {
                    acc.newFields.push(field)
                } else {
                    acc.templateFields.push(field)
                }
                return acc
            },
            {
                templateFields: [] as Field[],
                newFields: [] as Field[],
            },
        )
    }
}
